import Alpine from "alpinejs";
import gsap from "gsap";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";
import { CustomEase } from "gsap/CustomEase";
gsap.registerPlugin(MorphSVGPlugin);
gsap.registerPlugin(CustomEase);

const HOME_MV_IMAGE01_CLIP_PATHS = [
  "M695 241.508C695 316 674 386 606 438C544 486 448 508 347.5 508C235 508 128 494 62 430C18 387 0 319 0 241.508C0 158 30 88 95 44C157 2 250 0 347.5 0C462 0 574 24 638 86C680 127 695 180 695 241.508Z",
  "M695 244C690 312 655 378 590 430C526 482 438 506 347.5 508C248 510 144 490 78 426C28 378 0 314 0 242C0 166 42 96 112 52C174 14 260 4 347.5 0C452 -4 552 30 620 92C668 136 699 184 695 244Z",
  "M695 232C688 312 664 390 596 444C534 493 444 502 347.5 494C232 487 116 472 52 412C14 376 0 320 0 252C0 154 36 86 104 48C172 10 264 8 347.5 10C474 12 586 18 644 72C684 110 699 168 695 232Z",
  "M695 258C690 326 650 384 574 432C512 472 424 506 347.5 508C236 511 124 496 58 432C16 392 0 322 0 230C0 160 54 74 128 38C194 5 280 8 347.5 18C438 31 570 42 638 102C678 138 700 194 695 258Z",
  "M695 238C694 330 674 398 604 448C548 488 448 500 347.5 508C270 514 146 500 72 436C20 391 0 316 0 242C0 150 18 76 82 34C150 -10 268 -4 347.5 0C490 8 594 36 646 86C684 124 696 180 695 238Z",
  "M695 252C690 326 654 392 588 438C524 482 444 502 347.5 496C238 490 126 482 60 420C18 380 0 312 0 236C0 158 44 82 112 42C178 4 266 0 347.5 0C452 0 560 42 628 108C672 150 700 190 695 252Z",
];

const HOME_MV_IMAGE02_CLIP_PATHS = [
  "M411 142.623C411 186 397 230 358 260C320 290 265 300 205.5 300C138 300 74 292 35 254C10 230 0 187 0 142.623C0 92 16 50 54 24C90 0 148 0 205.5 0C272 0 334 14 374 52C399 76 411 108 411 142.623Z",
  "M411 145C408 184 388 222 350 254C312 286 260 298 205.5 300C146 302 84 290 46 252C16 222 0 184 0 143C0 98 24 58 66 32C104 9 156 2 205.5 0C268 -2 328 18 368 54C397 80 413 110 411 145Z",
  "M411 136C406 184 392 230 352 264C315 294 262 296 205.5 292C136 288 68 278 31 242C8 220 0 186 0 150C0 92 20 52 60 28C101 5 156 5 205.5 6C280 8 346 12 380 44C404 66 413 98 411 136Z",
  "M411 151C408 191 385 226 340 254C303 278 251 299 205.5 300C139 302 74 292 34 254C9 230 0 188 0 136C0 94 31 44 76 22C116 3 166 4 205.5 10C259 18 337 24 377 60C401 82 414 114 411 151Z",
  "M411 139C411 194 399 234 358 264C326 288 266 295 205.5 300C159 304 86 296 42 258C12 232 0 188 0 144C0 90 10 46 48 20C88 -5 158 -2 205.5 0C290 5 352 22 383 52C405 74 412 104 411 139Z",
  "M411 150C408 194 387 233 348 260C310 286 262 298 205.5 294C141 290 75 285 36 248C11 224 0 184 0 138C0 92 26 48 66 24C105 2 158 0 205.5 0C267 0 331 25 371 64C397 89 414 113 411 150Z",
];

const getRandomNumber = (min: number, max: number) =>
  Math.random() * (max - min) + min;

const getNextClipPath = (paths: string[], currentPath: string) => {
  const candidates = paths.filter((path) => path !== currentPath);
  return candidates[Math.floor(Math.random() * candidates.length)] || paths[0];
};

type HomeMvClipTarget = {
  pathEl: SVGPathElement;
  basePath: string;
  morphPaths: string[];
};

const playHomeMvIllust1FrameLoop = (
  mvIllust1Frame1: HTMLElement,
  mvIllust1Frame2: HTMLElement
) => {
  gsap
    .timeline({
      delay: 0,
      repeat: -1,
    })
    .to({}, {
      duration: 1,
      ease: "steps(1)",
      onStart: () => {
        gsap.set(mvIllust1Frame1, { opacity: 1 });
        gsap.set(mvIllust1Frame2, { opacity: 0 });
      },
    })
    .to({}, {
      duration: 1,
      ease: "steps(1)",
      onStart: () => {
        gsap.set(mvIllust1Frame1, { opacity: 0 });
        gsap.set(mvIllust1Frame2, { opacity: 1 });
      },
    });
};

const playHomeMvIllust2FrameLoop = (
  mvIllust2Frame1: HTMLElement,
  mvIllust2Frame2: HTMLElement,
  mvIllust2Frame3: HTMLElement
) => {
  gsap
    .timeline({
      repeat: -1,
    })
    .to({}, {
      duration: 1,
      ease: "steps(1)",
      onStart: () => {
        gsap.set(mvIllust2Frame1, { opacity: 0 });
        gsap.set(mvIllust2Frame2, { opacity: 0 });
        gsap.set(mvIllust2Frame3, { opacity: 1 });
      },
    })
    .to({}, {
      duration: 1,
      ease: "steps(1)",
      onStart: () => {
        gsap.set(mvIllust2Frame1, { opacity: 1 });
        gsap.set(mvIllust2Frame2, { opacity: 0 });
        gsap.set(mvIllust2Frame3, { opacity: 0 });
      },
    })
    .to({}, {
      duration: 1,
      ease: "steps(1)",
      onStart: () => {
        gsap.set(mvIllust2Frame1, { opacity: 0 });
        gsap.set(mvIllust2Frame2, { opacity: 1 });
        gsap.set(mvIllust2Frame3, { opacity: 0 });
      },
    });
};

const createRandomClipLoop = (clip: HomeMvClipTarget, delay = 0) => {
  let currentPath = clip.basePath;

  gsap.set(clip.pathEl, { attr: { d: clip.basePath } });

  const morphToNext = () => {
    const nextPath = getNextClipPath(clip.morphPaths, currentPath);
    currentPath = nextPath;

    gsap.to(clip.pathEl, {
      duration: getRandomNumber(1.8, 2.8),
      ease: "sine.inOut",
      morphSVG: {
        shape: nextPath,
        shapeIndex: 0,
      },
      onComplete: () => {
        gsap.delayedCall(getRandomNumber(1.4, 2.2), morphToNext);
      },
    });
  };

  gsap.delayedCall(delay, morphToNext);
};

export const initHomeMv = () => {
  Alpine.data("homeMv", () => {
    return {
      init() {
        const mvMountain = this.$refs.mvMountain;
        const mvImage01 = this.$refs.mvImage01;
        const mvImage02 = this.$refs.mvImage02;
        const mvImage01Clip = this.$refs.mvImage01Clip as unknown as SVGPathElement;
        const mvImage02Clip = this.$refs.mvImage02Clip as unknown as SVGPathElement;
        const mvImage01Image = this.$refs.mvImage01Image;
        const mvImage02Image = this.$refs.mvImage02Image;
        const mvIllust1 = this.$refs.mvIllust1 as HTMLElement;
        const mvIllust1Frame1 = this.$refs.mvIllust1Frame1 as HTMLElement;
        const mvIllust1Frame2 = this.$refs.mvIllust1Frame2 as HTMLElement;
        const mvIllust2 = this.$refs.mvIllust2 as HTMLElement;
        const mvIllust2Frame1 = this.$refs.mvIllust2Frame1 as HTMLElement;
        const mvIllust2Frame2 = this.$refs.mvIllust2Frame2 as HTMLElement;
        const mvIllust2Frame3 = this.$refs.mvIllust2Frame3 as HTMLElement;
        const mvCopy = this.$refs.mvCopy;
        const homeHeader = document.querySelector<HTMLElement>(".home-mv-header");
        const homeHeaderTargets = homeHeader ? [homeHeader] : [];

        const HOME_MV_MOUNTAIN_HORIZON_PATH =
          "M298.001 135C787.613 135 800.055 135 1228 135C1315.74 135 1385.31 135 1440 135V135H0V135C73.5892 135 170.252 135 298.001 135Z";
        const HOME_MV_MOUNTAIN_PATH =
          "M298.001 0C787.613 -0.00011001 800.055 171.551 1228 112.5C1315.74 100.393 1385.31 96.6589 1440 99.7363V135H0V32.5283C73.5892 12.3143 170.252 5.8466e-05 298.001 0Z";

        const homeMvImagePopEase = CustomEase.create(
          "homeMvImagePopEase",
          "M0,0 C0.12,0.72 0.22,1.18 0.42,1.08 C0.54,1.01 0.6,0.965 0.7,0.988 C0.8,1.012 0.9,1.004 1,1"
        );
        const lineInners = ([] as HTMLElement[]).slice.call(
          mvCopy.querySelectorAll(".home-mv-copy__line-inner")
        );
        const copyChars: HTMLElement[] = [];

        lineInners.forEach((lineInner) => {
          const text = lineInner.textContent || "";
          lineInner.textContent = "";

          text.split("").forEach((char) => {
            const charElement = document.createElement("span");
            charElement.className = "home-mv-copy__char";
            charElement.textContent = char === " " ? "\u00A0" : char;
            lineInner.appendChild(charElement);
            copyChars.push(charElement);
          });
        });

        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        const isMobile = window.matchMedia("(max-width: 749px)").matches;
    
        gsap.set(mvMountain, {  
          attr: {
            d: isMobile
              ? HOME_MV_MOUNTAIN_PATH
              : HOME_MV_MOUNTAIN_HORIZON_PATH,
          },
          yPercent: 0,
        });
        gsap.set(mvImage01Clip, {
          attr: { d: HOME_MV_IMAGE01_CLIP_PATHS[2] },
        });
        gsap.set(mvImage02Clip, {
          attr: { d: HOME_MV_IMAGE02_CLIP_PATHS[2] },
        });
        gsap.set(mvImage01, { 
          opacity: 0,
          scale: 0.6,
          yPercent: 30,
        });
        gsap.set(mvImage02, { 
          opacity: 0,
          scale: 0.5,
          yPercent: 20,
        });
        gsap.set(mvImage01Image, { 
          scale: 1.5,
          transformOrigin: "center center",
        });
        gsap.set(mvImage02Image, { 
          scale: 1.5,
          transformOrigin: "center center",
        });
        gsap.set([mvIllust1, mvIllust2], {
          opacity: 0,
          scale: 0.98,
          yPercent: 8,
          transformOrigin: "50% 100%",
        });
        gsap.set(mvIllust1Frame1, { opacity: 1 });
        gsap.set(mvIllust1Frame2, { opacity: 0 });
        gsap.set(mvIllust2Frame1, { opacity: 1 });
        gsap.set([mvIllust2Frame2, mvIllust2Frame3], { opacity: 0 });
        gsap.set(copyChars, {
          opacity: 0,
          yPercent: 80,
          transformOrigin: "50% 100%",
        });
        if (homeHeader) {
          gsap.set(homeHeader, {
            autoAlpha: 0,
            y: -12,
            pointerEvents: "none",
          });
        }

        if (prefersReducedMotion) {
          if (homeHeader) {
            gsap.set(homeHeader, {
              autoAlpha: 1,
              y: 0,
              pointerEvents: "auto",
              clearProps: "transform",
            });
          }
          gsap.set(mvMountain, {
            attr: { d: HOME_MV_MOUNTAIN_PATH },
            yPercent: 0,
          });
          gsap.set(mvImage01Clip, {
            attr: { d: HOME_MV_IMAGE01_CLIP_PATHS[0] },
          });
          gsap.set(mvImage02Clip, {
            attr: { d: HOME_MV_IMAGE02_CLIP_PATHS[0] },
          });
          gsap.set([mvImage01, mvImage02], {
            opacity: 1,
            scale: 1,
            rotate: 0,
            yPercent: 0,
          });
          gsap.set([mvImage01Image, mvImage02Image], {
            scale: 1,
          });
          gsap.set([mvIllust1, mvIllust2], {
            opacity: 1,
            scale: 1,
            yPercent: 0,
          });
          gsap.set(copyChars, {
            opacity: 1,
            yPercent: 0,
          });
          return;
        }
    
        const tl = gsap.timeline();
        const image01Start = isMobile ? 0 : 0.7;
        const image01ImageStart = isMobile ? 0 : 0.6;
        const image01ClipStart = isMobile ? 0.25 : 0.95;
        const image02Start = isMobile ? 0.1 : 0.8;
        const image02ImageStart = isMobile ? 0.1 : 0.7;
        const image02ClipStart = isMobile ? 0.35 : 1.05;
        const copyStart = isMobile ? 0.3 : 1.0;
    
        if (!isMobile) {
          tl.to(mvMountain, {
            duration: 1.2,
            ease: "power4.inOut",
            yPercent: 0,
            morphSVG: {
              shape: HOME_MV_MOUNTAIN_PATH,
              shapeIndex: 0,
            },
          });
        }

        tl.to(mvImage01, {
          opacity: 1,
          duration: 0.01,
          ease: "none",
        },
        image01Start
        )
        .to(
          homeHeaderTargets,
          {
            autoAlpha: 1,
            y: 0,
            pointerEvents: "auto",
            duration: 0.8,
            ease: "power2.out",
            onComplete: () => {
              gsap.set(homeHeaderTargets, { clearProps: "transform" });
            },
          },
          image01Start
        )
        .to(mvImage01, {
          yPercent: 0,
          duration: 1.2,
          ease: "expo.out",
        },
        image01Start
        )
        .to(mvImage01, {
          scale: 1,
          ease: homeMvImagePopEase,
          duration: 1.65,
        },
        image01Start
        )
        .to(mvImage01Image, {
          scale: 1,
          duration: 2.3,
          ease: homeMvImagePopEase,
        },
        image01ImageStart
        )
        .to(mvImage01Clip, {
          duration: 1.8,
          ease: "sine.inOut",
          morphSVG: {
            shape: HOME_MV_IMAGE01_CLIP_PATHS[0],
            shapeIndex: 0,
          },
        },
        image01ClipStart
        )
        .to(mvImage02, {
          opacity: 1,
          duration: 0.01,
          ease: "none",
        },
        image02Start
        )
        .to(mvImage02, {
          yPercent: 0,
          duration: 2,
          ease: "expo.out",
        },
        image02Start
        )
        .to(mvImage02, {
          scale: 1,
          ease: homeMvImagePopEase,
          duration: 1.65,
        },
        image02Start
        )
        .to(mvImage02Image, {
          scale: 1,
          duration: 2.3,
          ease: homeMvImagePopEase,
        },
        image02ImageStart
        )
        .to(mvImage02Clip, {
          duration: 1.8,
          ease: "sine.inOut",
          morphSVG: {
            shape: HOME_MV_IMAGE02_CLIP_PATHS[0],
            shapeIndex: 0,
          },
        },
        image02ClipStart
        )
        .to(copyChars, {
          opacity: 1,
          yPercent: 0,
          duration: 0.7,
          ease: "back.out(1.45)",
          stagger: 0.04,
        },
        copyStart
        )
        .to(
          [mvIllust1, mvIllust2],
          {
            opacity: 1,
            duration: 1.2,
            ease: "none",
            stagger: 0.08,
          },
          "-=0.9"
        )
        .to(
          [mvIllust1, mvIllust2],
          {
            scale: 1,
            yPercent: 0,
            duration: 1.2,
            ease: "back.out(1.4)",
            onComplete: () => {
              playHomeMvIllust1FrameLoop(mvIllust1Frame1, mvIllust1Frame2);
              playHomeMvIllust2FrameLoop(
                mvIllust2Frame1,
                mvIllust2Frame2,
                mvIllust2Frame3
              );
            },
          },
          "="
        )
        .call(() => {
          createRandomClipLoop({
            pathEl: mvImage01Clip,
            basePath: HOME_MV_IMAGE01_CLIP_PATHS[0],
            morphPaths: [
              HOME_MV_IMAGE01_CLIP_PATHS[2],
              HOME_MV_IMAGE01_CLIP_PATHS[5],
            ],
          });
          createRandomClipLoop(
            {
              pathEl: mvImage02Clip,
              basePath: HOME_MV_IMAGE02_CLIP_PATHS[0],
              morphPaths: HOME_MV_IMAGE02_CLIP_PATHS.slice(1),
            },
            0.4
          );
        });
      },
    };
  });
};
